/**
 * =========================================================
 * Vollebak — Search Modal Helpers (burger-menu style)
 * - Use the SAME positioning truth as the burger menu: --header-group-height
 * - No header re-positioning/visibility overrides (avoid sticky collisions)
 * - Arm scroll-to-top suppression BEFORE close paths (click/Escape/tap)
 * - Optional: lock background scroll while search is open
 *
 * Prototype patches (window.scrollTo, HTMLElement.prototype.focus,
 * Element.prototype.scrollIntoView) are applied here to suppress iOS
 * scroll-jump when Shopify's dialog-component opens/closes the search
 * modal. Each patch is guarded by a suppression flag set only inside a
 * short armed window around open/close, and restores native behaviour
 * otherwise. If Horizon updates break modal scroll, check these first.
 * =========================================================
 */

(() => {
  if (window.__vbSearchPatchedV2) return;
  window.__vbSearchPatchedV2 = true;

  const ROOT = document.documentElement;

// Keep a stable "scrolled" flag.
// IMPORTANT: when body is position:fixed (search open), window.scrollY often reports 0 on iOS.
// So while vb-search-open is set, we do NOT recompute vb-scrolled from window.scrollY.
function syncScrolledFlagFromWindow() {
  if (ROOT.classList.contains("vb-search-open")) return; // freeze during search-open
  document.body.classList.toggle("vb-scrolled", (window.scrollY || 0) > 0);
}
syncScrolledFlagFromWindow();
window.addEventListener("scroll", syncScrolledFlagFromWindow, { passive: true });



  const OPEN_BTN_SELECTOR =
    'button[on\\:click="#search-modal/showDialog"], button[onclick="#search-modal/showDialog"], .search-modal__button';

  const CLOSE_BTN_SELECTOR =
    ".predictive-search__close-modal-button, " +
    'button[on\\:click*="search-modal/hideDialog"], button[on\\:click*="search-modal/closeDialog"], ' +
    'button[onclick*="search-modal/hideDialog"], button[onclick*="search-modal/closeDialog"]';

  function getHeader() {
    return (
      document.querySelector("header-component#header-component") ||
      document.querySelector("header-component") ||
      document.querySelector("header")
    );
  }

  function getSearchDialog() {
    // Light DOM
    const dlg = document.querySelector("dialog.search-modal__content, .search-modal dialog");
    if (dlg) return dlg;

    // Shadow DOM (Horizon dialog-component)
    const host = document.querySelector("dialog-component#search-modal");
    if (host && host.shadowRoot) {
      const sdlg = host.shadowRoot.querySelector("dialog");
      if (sdlg) return sdlg;
    }
    return null;
  }

  function searchLooksOpen() {
    // Prefer actual dialog open state
    const dlg = getSearchDialog();
    if (dlg && dlg.open) return true;

    const host = document.querySelector("dialog-component#search-modal");
    if (host) {
      if (host.hasAttribute("open")) return true;
      if (host.getAttribute("aria-hidden") === "false") return true;
    }

    // Fallback to our own marker
    return ROOT.classList.contains("vb-search-open");
  }

  /**
   * ---------------------------------------------------------
   * 0) Guard window.scrollTo during a short suppression window
   * ---------------------------------------------------------
   */
  (() => {
    if (window.__vbScrollToPatched) return;
    window.__vbScrollToPatched = true;

    const orig = window.scrollTo.bind(window);
    window.__vbSuppressScrollToTopUntil = 0;

    window.__vbSuppressTopScroll = (ms = 800) => {
      window.__vbSuppressScrollToTopUntil = Date.now() + Math.max(0, ms);
    };

    window.scrollTo = function patchedScrollTo(x, y) {
      try {
        const now = Date.now();
        if (now < (window.__vbSuppressScrollToTopUntil || 0)) {
          if (typeof x === "object" && x) {
            const top = "top" in x ? Number(x.top) : null;
            const left = "left" in x ? Number(x.left) : null;
            if ((top === 0 || top === null) && (left === 0 || left === null)) return;
          } else {
            const xx = Number(x);
            const yy = Number(y);
            if ((xx === 0 || Number.isNaN(xx)) && (yy === 0 || Number.isNaN(yy))) return;
          }
        }
      } catch (e) {}
      return orig(x, y);
    };
  })();

  /**
   * ---------------------------------------------------------
   * 1) Prevent opener focus restore from scrolling page
   * ---------------------------------------------------------
   */
  // Lazy-install the focus/scrollIntoView patches only when suppression is armed
  // (i.e. while search is opening). This avoids a sitewide wrapper overhead that
  // was costing ~440ms on PDPs during initial load.
  window.__vbInstallSearchFocusPatch = function() {
    if (window.__vbPreventSearchFocusJumpPatched) return;
    window.__vbPreventSearchFocusJumpPatched = true;

    const origFocus = HTMLElement.prototype.focus;
    HTMLElement.prototype.focus = function patchedFocus(options) {
      try {
        const now = Date.now();
        const suppressUntil = window.__vbSuppressScrollToTopUntil || 0;
        if (this && this.matches && this.matches(OPEN_BTN_SELECTOR)) {
          options = { ...(options || {}), preventScroll: true };
        }
        if (now < suppressUntil) {
          options = { ...(options || {}), preventScroll: true };
        }
      } catch (e) {}
      return origFocus.call(this, options);
    };

    const origSIV = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function patchedScrollIntoView(arg) {
      try {
        const now = Date.now();
        const suppressUntil = window.__vbSuppressScrollToTopUntil || 0;
        if (this && this.matches && this.matches(OPEN_BTN_SELECTOR)) return;
        if (now < suppressUntil) return;
      } catch (e) {}
      return origSIV.call(this, arg);
    };
  };

  /**
   * ---------------------------------------------------------
   * 2) Set --vb-search-top using the burger menu variable first
   * ---------------------------------------------------------
   */
function setSearchTop() {
  const header = getHeader();
  let topPx = 0;

  // DESKTOP: always measure actual header bottom
  if (window.matchMedia("(min-width: 750px)").matches) {
    if (header) topPx = Math.round(header.getBoundingClientRect().bottom);
    ROOT.style.setProperty("--vb-search-top", `${Math.max(0, topPx)}px`);
    return;
  }

  // MOBILE:
  // - at top-of-page: use --header-group-height (keeps announcement behaviour correct)
  // - scrolled: measure actual header bottom (removes phantom announcement gap)
  const isScrolled = document.body.classList.contains("vb-scrolled");

  if (isScrolled && header) {
    topPx = Math.round(header.getBoundingClientRect().bottom);
  } else {
    const fromBody = getComputedStyle(document.body).getPropertyValue("--header-group-height").trim();
    const n = parseFloat(fromBody);
    if (Number.isFinite(n)) topPx = Math.round(n);
    else if (header) topPx = Math.round(header.getBoundingClientRect().bottom);
  }

  ROOT.style.setProperty("--vb-search-top", `${Math.max(0, topPx)}px`);
}



  /**
   * ---------------------------------------------------------
   * 3) Optional: lock background scroll while search open
   * (burger menu likely does similar; this keeps page from moving)
   * ---------------------------------------------------------
   */
  let lastScrollY = 0;
  let prevScrollRestoration = null;

  function lockScroll() {
    if (window.__vbInstallSearchFocusPatch) window.__vbInstallSearchFocusPatch();
    lastScrollY = window.scrollY || window.pageYOffset || 0;

  // Force vb-scrolled from the real scroll position we're freezing at
  document.body.classList.toggle("vb-scrolled", lastScrollY > 0);

    try {
      prevScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = "manual";
    } catch (e) {}

    ROOT.classList.add("vb-search-open");

    document.body.style.position = "fixed";
    document.body.style.top = `-${lastScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }

function unlockScroll() {
  // If we’re not locked, do nothing
  if (!ROOT.classList.contains("vb-search-open") && document.body.style.position !== "fixed") return;

  const y = lastScrollY || 0;

  // Arm suppression BEFORE anything else
  armCloseSuppression();

  const prevHtmlSB = document.documentElement.style.scrollBehavior;
  const prevBodySB = document.body.style.scrollBehavior;
  document.documentElement.style.scrollBehavior = "auto";
  document.body.style.scrollBehavior = "auto";

  // Remove lock markers FIRST
  ROOT.classList.remove("vb-search-open");

  // Release fixed body immediately
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";

  // Restore scroll
  try {
    window.scrollTo(0, y);
  } catch (e) {}

  // Restore scroll restoration
  try {
    if (prevScrollRestoration) history.scrollRestoration = prevScrollRestoration;
  } catch (e) {}

  // Restore scroll-behavior after
  requestAnimationFrame(() => {
    document.documentElement.style.scrollBehavior = prevHtmlSB || "";
    document.body.style.scrollBehavior = prevBodySB || "";

    // Now that we're back to normal scrolling, recompute vb-scrolled correctly
    syncScrolledFlagFromWindow();

    requestAnimationFrame(setSearchTop);
  });

}



  /**
   * ---------------------------------------------------------
   * 4) Arm suppression on ALL close paths (this is the flicker fix)
   * - close button click
   * - Escape key
   * - dialog close/cancel events
   * ---------------------------------------------------------
   */
  function armCloseSuppression() {
    if (typeof window.__vbSuppressTopScroll === "function") {
      window.__vbSuppressTopScroll(1200);
    }
  }

  // Close button (capture, before theme handlers)
  document.addEventListener(
    "click",
    (e) => {
      if (!searchLooksOpen()) return;

      const closeBtn = e.target.closest(CLOSE_BTN_SELECTOR);
      if (!closeBtn) return;

      armCloseSuppression();
    },
    true
  );

  // Escape key (capture, before dialog/component reacts)
  document.addEventListener(
    "keydown",
    (e) => {
      if (!searchLooksOpen()) return;
      if (e.key !== "Escape") return;

      armCloseSuppression();
    },
    true
  );

/**
 * ---------------------------------------------------------
 * 4.5) Mobile: tap in the header band closes search
 * (coordinate-based so it works even if the dialog/top-layer
 * swallows the target and you just get <html> as the element)
 * ---------------------------------------------------------
 */
function closeSearchNow_() {
  armCloseSuppression();

  const host = document.querySelector("dialog-component#search-modal");
  const dlg = getSearchDialog();

  try {
    if (host?.hideDialog) return host.hideDialog();
    if (host?.closeDialog) return host.closeDialog();
  } catch (e) {}

  try {
    if (dlg?.open && typeof dlg.close === "function") return dlg.close();
  } catch (e) {}
}

function getHeaderBottomPx_() {
  // Prefer actual header rect (most accurate)
  const header = getHeader();
  if (header) {
    const r = header.getBoundingClientRect();
    if (Number.isFinite(r.bottom)) return Math.max(0, Math.round(r.bottom));
  }

  // Fallback to theme variable
  const v = getComputedStyle(document.body).getPropertyValue("--header-group-height").trim();
  const n = parseFloat(v);
  if (Number.isFinite(n)) return Math.max(0, Math.round(n));

  return 80; // last resort
}

// Use pointerdown so it feels instant on iOS
document.addEventListener(
  "pointerdown",
  (e) => {
    if (!window.matchMedia("(max-width: 749px)").matches) return;
    if (!searchLooksOpen()) return;

    const headerBottom = getHeaderBottomPx_();

    // If the tap is inside the top "header band", close search
    // (clientY is viewport-relative)
    if (typeof e.clientY === "number" && e.clientY <= headerBottom) {
      closeSearchNow_();

      // behave like burger menu: tap closes, doesn't activate header controls
      e.preventDefault();
      e.stopPropagation();
    }
  },
  true
);

  
  // Dialog close/cancel → unlock scroll
  function bindDialogClose() {
    const dlg = getSearchDialog();
    if (!dlg || dlg.__vbBoundCloseV2) return;

    dlg.__vbBoundCloseV2 = true;

    const onDone = () => {
      // ensure suppression is armed even if close is programmatic
      armCloseSuppression();
      setTimeout(unlockScroll, 0);
    };

    dlg.addEventListener("close", onDone, true);
    dlg.addEventListener("cancel", onDone, true);
  }

  /**
   * ---------------------------------------------------------
   * 5) Open path
   * ---------------------------------------------------------
   */
  document.addEventListener(
    "click",
    (e) => {
      const openBtn = e.target.closest(OPEN_BTN_SELECTOR);
      if (!openBtn) return;

      setSearchTop();

      // On old browsers, inline script in theme.liquid handles open/close.
      try { if (!CSS.supports('selector(&)')) return; } catch(e) { return; }

      // Lock scroll AFTER measuring top
      lockScroll();

      // Dialog mounts async sometimes
      setTimeout(bindDialogClose, 0);
      setTimeout(setSearchTop, 50);
    },
    true
  );

  // Keep --vb-search-top accurate on layout changes
  setSearchTop();
  window.addEventListener("resize", setSearchTop, { passive: true });

  // Desktop only: keep --vb-search-top synced as announcement bar hides/reveals on scroll
if (window.matchMedia("(min-width: 750px)").matches) {
  let raf = 0;
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      setSearchTop();
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
}


  // If header size changes (announcement show/hide), track it
  const header = getHeader();
  if (header && "ResizeObserver" in window) {
    const ro = new ResizeObserver(() => requestAnimationFrame(setSearchTop));
    ro.observe(header);
  }

  // Also try to bind dialog close immediately if it exists
  bindDialogClose();

/**
 * ---------------------------------------------------------
 * FAILSAFE: If search dialog closes but close/cancel events don’t fire,
 * force unlock when the host loses [open] / aria-hidden flips true.
 * (Chrome Android sometimes misses close events.)
 * ---------------------------------------------------------
 */
(() => {
  const host = document.querySelector("dialog-component#search-modal");
  if (!host || host.__vbUnlockObserver) return;
  host.__vbUnlockObserver = true;

  const maybeUnlock = () => {
    const isOpen =
      host.hasAttribute("open") ||
      host.getAttribute("aria-hidden") === "false" ||
      host.querySelector?.("dialog[open]") ||
      getSearchDialog()?.open;

    if (!isOpen) {
      // Ensure we don't leave the page locked
      unlockScroll();
    }
  };

  const mo = new MutationObserver(maybeUnlock);
  mo.observe(host, { attributes: true, attributeFilter: ["open", "aria-hidden", "class", "style"] });

  // Also observe the actual <dialog> open state (light or shadow)
  const dlg = getSearchDialog();
  if (dlg && !dlg.__vbUnlockObserver) {
    dlg.__vbUnlockObserver = true;
    const moDlg = new MutationObserver(maybeUnlock);
    moDlg.observe(dlg, { attributes: true, attributeFilter: ["open"] });
  }


  // Also check on route changes / pagehide
  window.addEventListener("pagehide", unlockScroll, { passive: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") unlockScroll();
  });


  // One immediate check in case we loaded mid-state
  setTimeout(maybeUnlock, 0);
})();

  // If we somehow remain locked while the dialog is closed, first scroll fixes it
  window.addEventListener(
    "scroll",
    () => {
      if (!searchLooksOpen() && (ROOT.classList.contains("vb-search-open") || document.body.style.position === "fixed")) {
        unlockScroll();
      }
    },
    { passive: true }
  );


/**
 * ---------------------------------------------------------
 * 6) Patch Horizon dialog-component close methods
 * Ensures suppression is armed BEFORE Horizon restores focus/scroll.
 * ---------------------------------------------------------
 */
function patchDialogComponentClose() {
  const Ctor = customElements.get("dialog-component");
  if (!Ctor || Ctor.__vbClosePatched) return;

  Ctor.__vbClosePatched = true;

  const proto = Ctor.prototype;

  const wrap = (name) => {
    const orig = proto[name];
    if (typeof orig !== "function") return;

    proto[name] = function (...args) {
      try {
        // Only arm for the SEARCH dialog
        const isSearch =
          this?.id === "search-modal" ||
          this?.getAttribute?.("id") === "search-modal" ||
          this?.matches?.("dialog-component#search-modal") ||
          this?.querySelector?.("dialog.search-modal__content") ||
          this?.closest?.("dialog-component#search-modal");

        if (isSearch) {
          // If toggle is being used, only arm when it’s currently open (closing)
          if (name === "toggleDialog") {
            const isOpen =
              this.hasAttribute("open") ||
              this.getAttribute("aria-hidden") === "false" ||
              this.querySelector?.("dialog[open]");

            if (isOpen) armCloseSuppression();
          } else {
            // hideDialog / closeDialog always represent closing
            armCloseSuppression();
          }
        }
      } catch (e) {}

      return orig.apply(this, args);
    };
  };

  wrap("hideDialog");
  wrap("closeDialog");
  wrap("toggleDialog");
}

// Patch now if available, otherwise when it registers
patchDialogComponentClose();
customElements.whenDefined("dialog-component").then(patchDialogComponentClose);


})();
